import { useState } from 'react';
import { Globe, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CountryCellProps {
  value: string | null;
  onChange: (value: string | null) => void;
  readOnly?: boolean;
}

const COUNTRIES = [
  { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: 'GB', name: 'Reino Unido', flag: '🇬🇧' },
  { code: 'DE', name: 'Alemanha', flag: '🇩🇪' },
  { code: 'FR', name: 'França', flag: '🇫🇷' },
  { code: 'IT', name: 'Itália', flag: '🇮🇹' },
  { code: 'ES', name: 'Espanha', flag: '🇪🇸' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'CA', name: 'Canadá', flag: '🇨🇦' },
  { code: 'MX', name: 'México', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'CO', name: 'Colômbia', flag: '🇨🇴' },
  { code: 'JP', name: 'Japão', flag: '🇯🇵' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'IN', name: 'Índia', flag: '🇮🇳' },
  { code: 'AU', name: 'Austrália', flag: '🇦🇺' },
  { code: 'NZ', name: 'Nova Zelândia', flag: '🇳🇿' },
  { code: 'ZA', name: 'África do Sul', flag: '🇿🇦' },
  { code: 'RU', name: 'Rússia', flag: '🇷🇺' },
];

export function CountryCell({ value, onChange, readOnly }: CountryCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCountry = COUNTRIES.find((c) => c.code === value);
  
  const filteredCountries = COUNTRIES.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase())
  );

  if (readOnly && !selectedCountry) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  if (readOnly && selectedCountry) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-2xl">{selectedCountry.flag}</span>
        <span className="text-sm font-medium">{selectedCountry.name}</span>
      </div>
    );
  }

  if (isOpen) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[250px] max-h-96 overflow-y-auto">
        <div className="flex items-center gap-2 sticky top-0 bg-white pb-2">
          <Search className="w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar país..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-1">
          {filteredCountries.map((country) => (
            <div
              key={country.code}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
              onClick={() => {
                onChange(country.code);
                setIsOpen(false);
                setSearch('');
              }}
            >
              <span className="text-2xl">{country.flag}</span>
              <span className="text-sm">{country.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!selectedCountry) {
    return (
      <div
        className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer hover:text-gray-600"
        onClick={() => setIsOpen(true)}
      >
        <Globe className="w-4 h-4" />
        <span>Selecionar país</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
      onClick={() => setIsOpen(true)}
    >
      <span className="text-2xl">{selectedCountry.flag}</span>
      <span className="text-sm font-medium">{selectedCountry.name}</span>
    </div>
  );
}
